import { useCallback, useEffect, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import AssistantBlock from "@/components/assistant/AssistantBlocks";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Loader2, MessageSquarePlus, RotateCcw, SendHorizonal, Sparkles } from "lucide-react";
import { toast } from "sonner";

// The DFA Virtual Assistant conversation, shared by the parent and staff pages.
// Typed messages and quick actions both go to /assistant/chat; the server
// decides which tools this account may use, so nothing here grants access.

export const MAX_MESSAGE_LENGTH = 1000;
const MAX_COMPOSER_HEIGHT = 160;

const conversationKey = (userId) => `dfa_assistant_conversation:${userId || "me"}`;
const readStored = (key) => { try { return sessionStorage.getItem(key); } catch { return null; } };
const writeStored = (key, value) => {
  try { if (value) sessionStorage.setItem(key, value); else sessionStorage.removeItem(key); } catch { /* storage unavailable */ }
};

const Bubble = ({ role, children }) => <div className={`flex ${role === "user" ? "justify-end" : "justify-start"}`}>
  <div className={role === "user"
    ? "max-w-[85%] sm:max-w-xl rounded-2xl rounded-br-md bg-[#1B2C5A] text-white px-4 py-2 text-sm whitespace-pre-wrap break-words"
    : "w-full min-w-0"}>{children}</div>
</div>;

export default function AssistantChat({ subtitle, footnote, unavailableText, testId = "assistant-chat" }) {
  const { user } = useAuth();
  const storageKey = conversationKey(user?.id);
  const [config, setConfig] = useState(null);
  const [conversationId, setConversationId] = useState(() => readStored(storageKey));
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(null);
  const [resolvedActions, setResolvedActions] = useState({});
  const endRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    api.get("/assistant/config").then(({ data }) => setConfig(data)).catch(() => setConfig({ enabled: false }));
  }, []);
  useEffect(() => {
    if (!config?.enabled || !conversationId) return;
    api.get(`/assistant/conversations/${conversationId}`).then(({ data }) => setMessages(data.messages || []))
      .catch(() => { setConversationId(null); writeStored(storageKey, null); });
  }, [config?.enabled]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { endRef.current?.scrollIntoView?.({ behavior: "smooth", block: "end" }); }, [messages, busy, failed]);
  useEffect(() => {
    const input = inputRef.current;
    if (!input) return;
    input.style.height = "auto";
    input.style.height = `${Math.min(input.scrollHeight, MAX_COMPOSER_HEIGHT)}px`;
  }, [draft]);

  const submit = useCallback(async (request, label) => {
    setBusy(true); setFailed(null);
    setMessages((current) => [...current, { role: "user", text: label, blocks: [] }]);
    try {
      const { data } = await api.post("/assistant/chat", { ...request, conversation_id: conversationId || undefined });
      if (data.conversation_id !== conversationId) { setConversationId(data.conversation_id); writeStored(storageKey, data.conversation_id); }
      setMessages((current) => [...current, data.reply]);
    } catch (error) {
      setMessages((current) => current.slice(0, -1));
      setFailed({ request, label, message: error?.response ? formatApiError(error) : "We couldn't reach the school system. Check your connection and try again." });
    } finally {
      setBusy(false);
      inputRef.current?.focus?.();
    }
  }, [conversationId, storageKey]);

  const sendText = (text) => {
    const message = text.trim();
    if (!message || busy) return;
    setDraft("");
    submit({ message }, message);
  };
  const labelFor = (action) => (config?.quick_actions || []).find((item) => item.action.type === action.type)?.label || "Continue";
  const quick = (action, label) => submit({ action }, label || labelFor(action));

  const decide = async (actionId, decision) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/assistant/actions/${actionId}/${decision}`);
      setResolvedActions((current) => ({ ...current, [actionId]: decision === "confirm" ? "Payment request generated." : "Cancelled. Nothing was charged." }));
      setMessages((current) => [...current, { role: "user", text: decision === "confirm" ? "Generate Payment" : "Cancel", blocks: [] }, data.reply]);
    } catch (error) {
      const message = formatApiError(error);
      toast.error(message);
      setMessages((current) => [...current, { role: "assistant", text: message, blocks: [] }]);
    } finally { setBusy(false); }
  };

  const newConversation = () => {
    setConversationId(null); writeStored(storageKey, null);
    setMessages([]); setFailed(null); setResolvedActions({}); setDraft("");
    inputRef.current?.focus?.();
  };

  if (!config) return <div className="py-16 flex justify-center text-sm text-[#475569]"><Loader2 className="w-4 h-4 mr-2 animate-spin" />Loading assistant…</div>;
  if (!config.enabled) return <Card className="p-6 text-center text-sm text-[#475569]" data-testid="assistant-unavailable">{unavailableText}</Card>;

  const firstName = (user?.name || "").split(" ")[0];
  const quickActions = config.quick_actions || [];
  const remaining = MAX_MESSAGE_LENGTH - draft.length;
  // Wide enough on desktop for finance cards to show descriptions and amounts
  // side by side; text bubbles and cards keep their own readable maximums.
  return <div className="w-full max-w-5xl mx-auto" data-testid={testId}>
    <div className="flex items-center gap-2 mb-3">
      <div className="w-9 h-9 shrink-0 rounded-xl bg-[#1B2C5A] text-[#9DCD3F] grid place-items-center"><Sparkles className="w-4 h-4" /></div>
      <div className="min-w-0 flex-1"><h1 className="font-display text-xl text-[#1B2C5A] leading-tight">DFA Virtual Assistant</h1>
        <p className="text-xs text-[#475569]">{subtitle}</p></div>
      {messages.length > 0 && <Button type="button" size="sm" variant="ghost" disabled={busy} onClick={newConversation} className="shrink-0 text-[#1B2C5A]" data-testid="assistant-new-conversation">
        <MessageSquarePlus className="w-4 h-4 sm:mr-1" /><span className="hidden sm:inline">New conversation</span></Button>}
    </div>
    <Card className="flex flex-col h-[calc(100dvh-13rem)] min-h-[440px] lg:min-h-[560px] overflow-hidden">
      <div className="flex-1 overflow-y-auto p-3 sm:p-5 lg:px-8 space-y-3 bg-gradient-to-b from-white to-[#FDFBF7]" aria-live="polite" data-testid="assistant-messages">
        <Bubble role="assistant"><div className="inline-block max-w-2xl rounded-2xl rounded-bl-md bg-[#EEF3E5] px-4 py-3 text-sm text-[#1B2C5A]">
          Assalamu Alaikum{firstName ? `, ${firstName}` : ""}. How may I assist you today? Type your message below{quickActions.length ? " or choose a quick action" : ""}.
        </div></Bubble>
        {messages.map((message, index) => <Bubble key={index} role={message.role}>
          {message.role === "user" ? message.text : <>
            {message.text && <div className="inline-block max-w-2xl rounded-2xl rounded-bl-md bg-[#EEF3E5] px-4 py-2 text-sm text-[#1B2C5A] whitespace-pre-line">{message.text}</div>}
            {message.blocks?.length > 0 && <div className="max-w-3xl" data-testid="assistant-cards">
              {message.blocks.map((block, blockIndex) => <AssistantBlock key={blockIndex} block={block} busy={busy} resolvedActions={resolvedActions}
                onQuick={(action, label) => quick(action, label)} onSend={sendText} onConfirm={(id) => decide(id, "confirm")} onCancel={(id) => decide(id, "cancel")} />)}
            </div>}
          </>}
        </Bubble>)}
        {busy && <div className="flex items-center gap-2 text-xs text-[#475569]" data-testid="assistant-thinking"><Loader2 className="w-3.5 h-3.5 animate-spin" />Checking the school records…</div>}
        {failed && <div className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-800 flex flex-wrap items-center justify-between gap-2" role="alert">
          <span>{failed.message || "Something went wrong."}</span>
          <Button size="sm" variant="outline" disabled={busy} onClick={() => submit(failed.request, failed.label)}><RotateCcw className="w-3.5 h-3.5 mr-1" />Retry</Button>
        </div>}
        <div ref={endRef} />
      </div>
      <div className="border-t bg-white">
        {quickActions.length > 0 && <div className="flex gap-2 overflow-x-auto sm:flex-wrap px-2 sm:px-3 lg:px-8 pt-2 pb-1 scrollbar-thin" data-testid="assistant-quick-actions" aria-label="Quick actions">
          {quickActions.map((option) => <button key={option.action.type} type="button" disabled={busy} onClick={() => quick(option.action, option.label)}
            className="shrink-0 whitespace-nowrap rounded-full border border-[#1B2C5A]/15 bg-white px-3 py-1 text-xs text-[#1B2C5A] hover:border-[#9DCD3F] disabled:opacity-50">{option.label}</button>)}
        </div>}
        <form className="p-2 sm:p-3 lg:px-8 flex items-end gap-2" onSubmit={(event) => { event.preventDefault(); sendText(draft); }} data-testid="assistant-composer">
          <div className="flex-1 min-w-0">
            <textarea ref={inputRef} rows={1} maxLength={MAX_MESSAGE_LENGTH} value={draft}
              onChange={(event) => setDraft(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter" && !event.shiftKey && !event.nativeEvent?.isComposing) { event.preventDefault(); sendText(draft); }
              }}
              placeholder="Type your message…" enterKeyHint="send"
              className="block w-full resize-none rounded-xl border border-[#1B2C5A]/15 px-3 py-2 text-base sm:text-sm leading-6 focus:outline-none focus:ring-2 focus:ring-[#9DCD3F]"
              aria-label="Message the DFA Virtual Assistant" data-testid="assistant-input" />
            <div className="flex justify-between px-1 pt-1 text-[11px] text-[#475569]/70">
              <span className="hidden sm:inline">Enter to send · Shift+Enter for a new line</span>
              {remaining < 100 && <span className={remaining <= 0 ? "text-red-600" : ""}>{remaining} characters left</span>}
            </div>
          </div>
          <Button type="submit" disabled={busy || !draft.trim()} className="mb-5 bg-[#1B2C5A] hover:bg-[#14224A] text-white" data-testid="assistant-send">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <SendHorizonal className="w-4 h-4" />}
            <span className="ml-1">{busy ? "Sending" : "Send"}</span>
          </Button>
        </form>
      </div>
    </Card>
    {footnote && <p className="text-[11px] text-[#475569]/70 text-center mt-2">{footnote}</p>}
  </div>;
}
